import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'reducers';
import { addDays } from 'date-fns';
import * as routerActions from 'router/actions';
import { RouterLocation } from 'router/location';
import { DATE_FORMAT } from 'utils/constants';
import {
  Usps,
  usePromoCodeValidator,
} from 'components/Payment';
import {
  Box,
  Text,
  TextDate,
  Price,
  LinkButton,
  ClientSideComponent,
} from 'components';
import { useI18n } from 'components/I18n';
import { TOfferPurchasable } from 'views/Checkout/types';
import { isTVOD } from 'utils/helpers';
import { formatEntitlementDuration } from 'utils/time';
import PromoCodeModal from '../PromoCodeModal';
import Discount from './Discount';

interface IOfferInfoProps {
  promoCode?: string;
  offer: TOfferPurchasable;
  price: number,
  trialDays: number,
  reducedPrice: number,
  promoCodeDiscount?: number,
  upgradeRefundDiscount?: number,
  campaignName?: string,
  usps?: string[],
  isPromoCodeAllowed: boolean,
  packageId: string,
  hasDiscount: boolean,
  recurringPeriod?: string,
  promoCodeExpiry?: string,
  date: Date,
  originalOfferId?: string,
}

function OfferInfo(props: IOfferInfoProps) {
  const {
    promoCode,
    offer,
    price,
    trialDays = 0,
    reducedPrice,
    promoCodeDiscount,
    upgradeRefundDiscount,
    campaignName,
    usps,
    isPromoCodeAllowed,
    packageId,
    hasDiscount,
    recurringPeriod,
    promoCodeExpiry,
    date,
    originalOfferId,
  } = props;

  const i18n = useI18n();
  const dispatch = useDispatch();
  const {
    location,
  } = useSelector(state => ({
    location: state.router.location,
  }));
  const [showModal, setShowModal] = useState(false);
  const freeTrialExpiry = useRef(addDays(date, trialDays));
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const onPromoCode = (newPromoCode: string | undefined) => {
    dispatch(routerActions.push({
      ...location,
      query: {
        ...(location?.query || {}),
        promoCode: newPromoCode,
      },
    } as RouterLocation));
    setShowModal(false);
  };

  const validatePromocode = usePromoCodeValidator({
    offerId: packageId,
    originalOfferId: originalOfferId,
    onSuccess(result: any) {
      onPromoCode(result.promoCode);
    },
  });

  return (
    <Box
      p="large"
      sm-p="xlarge"
      borderRadius="paper"
      bg="paperBackground"
      fontSize="medium"
      innerRef={containerRef}
      tabIndex={0}
    >
      <Box mb="small" spaceBetween>
        <Box mr="small">
          <Text bold wordBreak="break-word">
            {offer.title}
          </Text>
        </Box>
        <Text
          id="payment.price"
          bold
          values={{
            period: recurringPeriod,
            price: (
              <Price key="price" price={price} currency={offer.currency} />
            ),
          }}
        />
      </Box>

      {!!trialDays && (
        <Box mb="small" spaceBetween>
          <Box mr="small">
            <Text
              id="checkout.freeTrialPrice"
              bold
              values={{
                date: (
                  <TextDate
                    key="date"
                    value={freeTrialExpiry.current}
                    options={DATE_FORMAT}
                  />
                ),
              }}
            />
          </Box>
          <Price
            price={0}
            currency={offer.currency}
            bold
          />
        </Box>
      )}

      {!!upgradeRefundDiscount && (
        <Box
          mb="small"
          spaceBetween
          aria-label={`${i18n.formatText('checkout.refundText')} ${i18n.formatCurrency(-upgradeRefundDiscount, offer.currency)}`}
          role="group"
        >
          <Box mr="small">
            <Text id="checkout.refundText" />
          </Box>
          <Price
            color="reducedPrice"
            price={-upgradeRefundDiscount}
            currency={offer.currency}
            bold
          />
        </Box>
      )}

      <Usps
        mt="large"
        mb="fine"
        usps={offer.usps}
        type="bullet"
        size="small"
        alignItems="start"
      />

      {(offer.__typename === 'PassType' || offer.__typename === 'RentType') && offer.entitlementDurationSec && (
        <Box mb="medium">
          <Text
            fontSize="normal"
            id="checkout.offerDuration"
            values={formatEntitlementDuration(offer.entitlementDurationSec)}
          />
        </Box>
      )}

      {originalOfferId && (
        <Box mb="medium">
          <Text
            id="checkout.discardPromotion"
            fontSize="small"
            isHtml
            values={{ offerName: offer.title }}
          />
        </Box>
      )}

      {promoCode && !!promoCodeDiscount && (
        <Discount
          campaignName={campaignName || ''}
          promoCode={promoCode}
          discountAmount={promoCodeDiscount}
          usps={usps}
          currency={offer.currency}
          onRemoveDiscount={isPromoCodeAllowed ? () => onPromoCode(undefined) : undefined}
          validFrom={freeTrialExpiry.current}
          validTo={promoCodeExpiry}
          type={offer.__typename}
        />
      )}

      {isPromoCodeAllowed && !promoCodeDiscount && (
        <Box>
          <LinkButton inline onClick={() => setShowModal(true)}>
            <Text
              id="checkout.promoCodeButton"
              bold
            />
          </LinkButton>

          {showModal && (
            <PromoCodeModal
              offer={offer}
              validatePromocode={validatePromocode}
              onClose={() => setShowModal(false)}
            />
          )}
        </Box>
      )}

      {hasDiscount && (
        <Box
          mt="xlarge"
          spaceBetween
          aria-label={`${i18n.formatText('payment.total')} ${i18n.formatCurrency(trialDays ? 0 : reducedPrice, offer.currency)}`}
          role="group"
        >
          <Box mr="small">
            <Text bold id="payment.total" />
          </Box>
          <Price
            price={trialDays ? 0 : reducedPrice}
            currency={offer.currency}
            bold
          />
        </Box>
      )}

      {(
        ((!!trialDays && !promoCodeExpiry) || promoCodeExpiry)
        && !isTVOD(offer.__typename)
      ) && (
        <Box mt="fine">
          <ClientSideComponent>
            <Text
              id="checkout.fullPriceApply"
              fontSize="small"
              values={{
                date: i18n.formatDate(promoCodeExpiry || freeTrialExpiry.current, DATE_FORMAT),
              }}
            />
          </ClientSideComponent>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(OfferInfo);
